// Shim to integrate react-rails with modern Turbo (Hotwire)
// Based on https://github.com/reactjs/react-rails/blob/master/react_ujs/src/events/turbolinks.js
//
// react-rails only knows about turbolinks:load events, not modern Turbo events.
// This shim bridges that gap by listening to Turbo events and mounting React components.

let ReactRailsUJS = null
let healTimer = null

// Long enough that a normal navigation has rendered (and cancelled this) well before it fires, so
// the healing remount below costs nothing on the happy path.
const HEAL_AFTER_MS = 3000

// Smart mount: only mount components that haven't been mounted yet.
// This prevents remounting existing components with stale props from data-react-props,
// which would cause loss of current state (e.g., MultiSelect selections).
function handleMount() {
  if (!ReactRailsUJS) return

  clearTimeout(healTimer)

  const components = document.querySelectorAll('[data-react-class]')
  components.forEach(element => {
    // Use ReactRailsUJS.findRoot to check if component is already mounted.
    // This is more reliable than checking childNodes since it uses the
    // library's internal root tracking.
    if (!ReactRailsUJS.findRoot(element)) {
      ReactRailsUJS.mountComponents(element.parentNode)
    }
  })
}

function handleUnmount() {
  if (!ReactRailsUJS) return

  ReactRailsUJS.unmountComponents()

  // Turbo snapshots the *live* DOM, so that unmount strips React from the page the user is still
  // looking at. A render normally follows within milliseconds and remounts everything. When one
  // never arrives the page is left inert — every react-select and datepicker on it dead — with no
  // error to show for it. Turbo 8.0.21 caused exactly that by turning fragment changes into
  // snapshot-then-visit; assume the next version finds another way and heal instead of trusting
  // the invariant. handleMount cancels this, so it only ever fires when no render came.
  clearTimeout(healTimer)
  healTimer = setTimeout(handleMount, HEAL_AFTER_MS)
}

function handleStreamRender(event) {
  // Schedule mount after the stream action completes
  // The event.target is the turbo-stream element with the action
  const originalRender = event.detail.render
  event.detail.render = async function (streamElement) {
    await originalRender(streamElement)
    // Mount components after stream action completes
    handleMount()
  }
}

export function setup(ujs) {
  ReactRailsUJS = ujs

  // Turbo Drive navigation events (similar to turbolinks:load)
  document.addEventListener('turbo:load', handleMount)

  // Turbo renders - fires after Turbo renders content
  document.addEventListener('turbo:render', handleMount)

  // Turbo Frame renders
  document.addEventListener('turbo:frame-render', handleMount)

  // Turbo Frame loads (fires after frame content is loaded)
  document.addEventListener('turbo:frame-load', handleMount)

  // Turbo Stream renders - hook into the render process
  document.addEventListener('turbo:before-stream-render', handleStreamRender)

  // Custom event for non-Turbo DOM insertions (e.g., nested forms)
  document.addEventListener('react:mount', handleMount)

  // Cleanup before caching to prevent stale React state
  document.addEventListener('turbo:before-cache', handleUnmount)

  // Restoring from the browser's bfcache does not fire a Turbo render.
  window.addEventListener('pageshow', handleMount)
}

export function teardown() {
  document.removeEventListener('turbo:load', handleMount)
  document.removeEventListener('turbo:render', handleMount)
  document.removeEventListener('turbo:frame-render', handleMount)
  document.removeEventListener('turbo:frame-load', handleMount)
  document.removeEventListener('turbo:before-stream-render', handleStreamRender)
  document.removeEventListener('react:mount', handleMount)
  document.removeEventListener('turbo:before-cache', handleUnmount)
  window.removeEventListener('pageshow', handleMount)
  clearTimeout(healTimer)
  ReactRailsUJS = null
}

export default { setup, teardown }
